/**
 * MessageContext - Provides context for message module configuration
 *
 * Provides:
 * - Route context (isGlobal, isFromCase, isFromLead, isFromClientPortal)
 * - Case details
 * - Helper hooks for case-specific logic
 */

import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

const MessageContext = createContext(null);

/**
 * MessageProvider - Wraps message routes with context
 */
export function MessageProvider({ children, caseDetails, isFromLead, isGlobal, isFromClientPortal, isFromCase, clientPortalDashboard }) {
  const contextValue = useMemo(
    () => ({
      // Route flags
      isGlobal: Boolean(isGlobal),
      isFromCase: Boolean(isFromCase),
      isFromLead: Boolean(isFromLead),
      isFromClientPortal: Boolean(isFromClientPortal),
      clientPortalDashboard: Boolean(clientPortalDashboard),

      // Case details (if viewing from case/lead module)
      caseDetails: caseDetails || null,
      caseId: caseDetails?.case_id || null,
      caseName: caseDetails?.case_description?.case_name || null,
    }),
    [caseDetails, isFromLead, isGlobal, isFromClientPortal, isFromCase, clientPortalDashboard]
  );

  return <MessageContext.Provider value={contextValue}>{children}</MessageContext.Provider>;
}

MessageProvider.propTypes = {
  children: PropTypes.node.isRequired,
  caseDetails: PropTypes.shape({
    case_id: PropTypes.string,
    status: PropTypes.string,
    case_description: PropTypes.shape({
      case_name: PropTypes.string,
    }),
  }),
  isFromLead: PropTypes.bool,
  isGlobal: PropTypes.bool,
  isFromClientPortal: PropTypes.bool,
  isFromCase: PropTypes.bool,
  clientPortalDashboard: PropTypes.bool,
};

MessageProvider.defaultProps = {
  caseDetails: null,
  isFromLead: false,
  isGlobal: false,
  isFromClientPortal: false,
  isFromCase: false,
  clientPortalDashboard: false,
};

/**
 * useMessageContext - Main hook to access message context
 */
export function useMessageContext() {
  const context = useContext(MessageContext);

  if (!context) {
    throw new Error('useMessageContext must be used within a MessageProvider');
  }

  return context;
}

/**
 * useMessageConfig - Get route configuration
 */
export function useMessageConfig() {
  const { isGlobal, isFromCase, isFromLead, isFromClientPortal, clientPortalDashboard } = useMessageContext();

  return {
    isGlobal,
    isFromCase,
    isFromLead,
    isFromClientPortal,
    clientPortalDashboard,
  };
}

/**
 * useMessageCase - Get case details and status
 */
export function useMessageCase() {
  const { caseDetails, caseId, caseName } = useMessageContext();

  const isCaseClosed = useMemo(() => {
    return caseDetails?.status === 'CLOSE' || caseDetails?.status === 'CLOSE_ACTIVE';
  }, [caseDetails?.status]);

  return {
    caseDetails,
    caseId,
    caseName,
    isCaseClosed,
  };
}
